import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  coachSlugIsBookableThisWeek,
  getBookThisWeekCoachSlugs,
  getBookThisWeekObjectIds,
} from '@lib/coaches-directory/book-this-week'

/**
 * Directory InstantSearch hits come from coachProfiles, which does not store
 * this-week availability. Loads private-offering slugs (`availability_windows:
 * this_week`) for card badges, and geo-matched coachProfiles objectIDs so
 * Availability can filter. ObjectID resolution only runs when the filter is on.
 */
export function useBookThisWeekDirectory({
  aroundLatLng,
  aroundRadius,
  bookThisWeekOnly = false,
  initialObjectIDs,
} = {}) {
  const [objectIDs, setObjectIDs] = useState(() => initialObjectIDs || [])
  const [ready, setReady] = useState(() => (
    Array.isArray(initialObjectIDs) || !bookThisWeekOnly
  ))
  const skipPendingOnceRef = useRef(Array.isArray(initialObjectIDs))

  useEffect(() => {
    if (!bookThisWeekOnly) {
      setReady(true)
      return undefined
    }
    let cancelled = false
    const skipPending = skipPendingOnceRef.current
    skipPendingOnceRef.current = false
    if (!skipPending) {
      setReady(false)
    }
    getBookThisWeekObjectIds({ aroundLatLng, aroundRadius })
      .then((ids) => {
        if (!cancelled) {
          setObjectIDs(ids)
          setReady(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReady(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [aroundLatLng, aroundRadius, bookThisWeekOnly])

  return {
    objectIDs,
    ready,
  }
}

export default function useCoachesBookThisWeek() {
  const [thisWeekSlugs, setThisWeekSlugs] = useState(() => new Set())

  useEffect(() => {
    let cancelled = false
    getBookThisWeekCoachSlugs()
      .then((slugs) => {
        if (!cancelled) setThisWeekSlugs(slugs)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  return useCallback(
    (slug) => coachSlugIsBookableThisWeek(slug, thisWeekSlugs),
    [thisWeekSlugs],
  )
}
